import type { FC, MouseEvent } from "react";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import cn from "classnames";
import { useSelector } from "react-redux";
import { Popover } from "@litres/ui-kit/popover";
import { type RootState } from "@litres-core/store";
import debounce from "lodash/debounce";
import { v4 as uuidv4 } from "uuid";

import { isThisYear, parseISO } from "@litres/utils/date-fns";
import { useSafemode } from "@litres-modules/safemode";
import {
  deleteCookie,
  findFocussableNextSiblingElem,
  findFocussablePrevSiblingElem,
  focusFirstInElement,
  getCookie,
  getFromLocalStorage,
  hasWindow,
  removeFromLocalStorage,
  setCookie,
} from "@litres/utils";
import {
  useEventListener,
  useLocation,
  useWindowDimensions,
} from "@litres-libs/global-hooks";
import { useTranslation } from "@litres-libs/i18n";
import { SimpleSearch } from "@litres-modules/simple-search";
import type {
  ILastSearch,
  ISearchHistoryItem,
  SearchingResults,
} from "./types";
import { KeyCodes, SearchErrors } from "./constants";
import {
  getEditDistance,
  getLastSearchResults,
  getSearchPlaceholder,
  longestCommonSubstring,
  saveLastSearchResultsToLocalStorage,
  trackerSearchClickInput,
} from "./utils";
import { SearchHistory } from "./SearchHistory/SearchHistory";
import { ResultsList } from "./ResultsList/ResultsList";
import { isFocusElement } from "../../utils/domHelpers";
import { useApi } from "../../hooks/useApi";
import styles from "./NewSearch.module.scss";
import { SearchForm } from "./SearchForm";
import { useRefCallback } from "../../hooks/useRefCallback";

interface ISearchProps {
  isDesktop?: boolean;
}

const useEnhancedEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;

export const NewSearch: FC<ISearchProps> = ({ isDesktop = true }) => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search?.substr(1));
  const [searchValue, setSearchValue] = useState<string>("");
  const [isSearchWidth, setSearchWidth] = useState<number | null>(null);
  const [isError, setError] = useState<boolean>(false);
  const [isHistoryRefreshed, setHistoryRefreshed] = useState<boolean>(false);
  const [isPopoverVisibility, setPopoverVisibility] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [lastSearch, setLastSearch] = useState<ILastSearch[]>(
    getLastSearchResults()
  );
  const [isUserHasHistory, setUserHasHistory] = useState<boolean>(
    !!(hasWindow && getFromLocalStorage("last_search"))
  );
  const [searchID, setSearchID] = useState<string>("");

  const {
    history = [],
    searchResults,
    query: searchPhrase,
  } = useSelector((state: RootState) => state.global.search);

  const isUserAnonymous = useSelector(
    (state: RootState) => state.foundationUser.id === 0
  );

  const {
    getSearchData,
    getSearchHistory,
    saveSearchQuery,
    refreshSearchResults,
  } = useApi();

  const { width, height } = useWindowDimensions();

  const [resultsList, resultsListRefCallback] =
    useRefCallback<HTMLUListElement>();

  const { query: searchQuery, appliedCorrection } = useSelector(
    (state: RootState) => state.global.searchPage
  );

  useEffect(() => {
    if (!checkSearchValue(searchValue)) {
      refreshSearchResults();
    }
  }, [searchValue]);

  useEffect(() => {
    setSearchValue(
      appliedCorrection ? appliedCorrection.to_query : searchQuery
    );
  }, [searchQuery, appliedCorrection]);

  useEffect(() => {
    setSearchValue(searchParams.get("q") || "");

    if (isUserAnonymous || (isUserHasHistory && !needToUpdateLastSearch())) {
      // работаем с localStorage
      return;
    }

    getSearchHistory([0, 20]);
  }, []);

  useEffect(() => {
    if (history.length === 0) {
      return;
    }

    injectDBHistoryIntoStorage();
  }, [history]);

  useEffect(() => {
    saveLastSearchResultsToLocalStorage(lastSearch);
    setLastSearchCookie();
    if (lastSearch.length > 0) {
      setUserHasHistory(true);
    }
  }, [lastSearch]);

  useEffect(() => {
    if (isHistoryRefreshed) {
      refreshHistory();
    }
  }, [isHistoryRefreshed]);

  // Подписываемся на изменения safeModeStatus, если он поменяется то запросим новые данные
  const { safeModeStatus, isSafeModeEnabled } = useSafemode();
  useEffect(() => {
    if (isSafeModeEnabled) {
      getSearchResult(searchValue);
    }
  }, [safeModeStatus]);

  // запрещаем прокрутку страницы при открытом окне
  useEffect(() => {
    document.body.style.overflow = isPopoverVisibility ? "hidden" : "";

    return (): void => {
      document.body.style.overflow = "";
    };
  }, [isPopoverVisibility, setPopoverVisibility]);

  const refreshHistory = (): void => {
    if (hasWindow) {
      removeFromLocalStorage("last_search");
      setLastSearch([]);
      deleteCookie("last_search");
      setUserHasHistory(false);
    }
  };

  const lastSearchAdapter = (search: ISearchHistoryItem[]): ILastSearch[] => {
    return search.map(item => {
      return {
        value: item.s_search,
        source: "server",
      } as ILastSearch;
    });
  };

  const containsRepeatWords = (
    searchPhrase: string,
    lastSearch: string
  ): boolean => {
    return (
      searchPhrase === lastSearch ||
      (getEditDistance(searchPhrase, lastSearch) <= 5 &&
        longestCommonSubstring(searchPhrase, lastSearch) >= 3)
    );
  };

  /**
   * Изменит source саджеста из истории поиска с "client" на "server"
   */
  const setLastSearchResultToServer = (val: string): void => {
    const valIndex = getIndexOfLastSearchItem(val);

    // если такого индекса не нашлось — тихо завершаем работу
    if (valIndex === -1) {
      return;
    }

    setLastSearch(currentLastSearchValue => {
      const lastSearchResults = currentLastSearchValue.slice();
      lastSearchResults[valIndex].source = "server";
      return lastSearchResults;
    });
  };

  /**
   * Отдаст индекс объекта из истории поиска, если такой имеется (иначе -1)
   */
  const getIndexOfLastSearchItem = (val: string): number => {
    return lastSearch.map(obj => obj.value).indexOf(val);
  };

  const setLastSearchCookie = (): void => {
    setCookie("last_search", String(+new Date()), {
      expires: 1800,
    });
  };

  /**
   * Добавить новый поисковый запрос в хранилище
   * Запрос будет добавлен с источником "client"
   */
  const addItemToLastSearchStorage = (
    newValue: string,
    position?: number
  ): void => {
    setLastSearchCookie();
    // повторно записи не добавляем
    if (getIndexOfLastSearchItem(newValue) !== -1) {
      return;
    }

    const newItem: ILastSearch = {
      value: newValue,
      source: "client",
    };

    setLastSearch(currentLastSearchValue => {
      const lastSearchResults = currentLastSearchValue.slice();
      if (position || position === 0) {
        lastSearchResults[position] = newItem;
      } else {
        lastSearchResults.push(newItem);
      }
      if (lastSearchResults.length > 10) {
        lastSearchResults.shift();
      }
      return lastSearchResults;
    });
  };

  /**
   * Удалить поисковый запрос из хранилища
   */
  const deleteItemFromLastSearchStorage = (value: string): void => {
    setLastSearch(currentLastSearchValue => {
      const lastSearchResults = currentLastSearchValue.slice();
      return lastSearchResults.filter(searchItem => searchItem.value !== value);
    });
  };

  /**
   * Проверка, нужно ли обновить результаты последнего поиска.
   * Если с последней проверки прошло больше 20 минут - обновляем.
   */
  const needToUpdateLastSearch = (): boolean => {
    const lastSearchDate = getCookie("last_search");
    if (lastSearchDate) {
      const minutesDiff = (Number(new Date()) - Number(lastSearchDate)) / 60000;
      if (minutesDiff < 20) {
        return false;
      }
    }
    return true;
  };

  const injectDBHistoryIntoStorage = (): void => {
    const lastYearRequests = history!.filter(
      (historyItem: ISearchHistoryItem) => {
        const { time } = historyItem;

        const parsedDate = parseISO(time);

        return isThisYear(parsedDate);
      }
    );

    // Возможно стоит отрезать лишнее внутри компонента истории
    const lastNineRequests = lastYearRequests.filter(
      (historyItem: ISearchHistoryItem, index: number) => index < 9
    );

    setLastSearch(lastSearchAdapter(lastNineRequests));
  };

  const handleOnChange = (value: string): void => {
    setSearchValue(value);
    debouncedChangeHandler(value);
  };

  const handleClear = (): void => {
    setSearchValue("");
  };

  const debouncedChangeHandler = useCallback(
    debounce(nextValue => {
      getSearchResult(nextValue);
    }, 400),
    [lastSearch]
  );

  const getSearchResult = (value: string): void => {
    if (checkSearchValue(value)) {
      getSearchData(value);
      setHistoryRefreshed(false);
      setSearchID(uuidv4());

      for (let i = 0; i < lastSearch.length; i++) {
        if (containsRepeatWords(value, lastSearch[i].value)) {
          addItemToLastSearchStorage(value, i);
          return;
        }
      }
      if (value.length > 2) {
        addItemToLastSearchStorage(value);
      }
    }
  };

  const handleOnClick = (): void => {
    setPopoverVisibility(true);
    trackerSearchClickInput(window.location.href);
  };

  useEnhancedEffect(() => {
    if (searchInputRef.current) {
      setSearchWidth(searchInputRef.current.offsetWidth);
    }
  }, []);

  useEffect(() => {
    if (searchInputRef.current) {
      setSearchWidth(searchInputRef.current.offsetWidth);
    }
  }, [width]);

  useEffect(() => {
    if (searchValue.length === 0 || checkSearchValue(searchValue)) {
      setError(false);
    }
  }, [searchValue, setError, isError]);

  const checkSearchValue = (value: string): boolean => {
    /* eslint-disable no-useless-escape */
    return (
      (value &&
        value.replace(/[\s\\\/~#$%^&*(),._!@"№;:?=«»'`\[\]{}+\-<>]/g, "")
          .length > 0) ||
      /^\d+$/.test(value)
    );
  };

  const searchContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPopoverVisibility(false);
  }, [width, height]);

  const handleSearchFormSubmit = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      event.preventDefault();

      setPopoverVisibility(false);

      if (searchValue === "" && searchInputRef.current) {
        searchInputRef.current.focus();
        searchInputRef.current.setSelectionRange(0, 0);

        return;
      }

      if (!isUserAnonymous) {
        saveSearchQuery(searchValue);
        setLastSearchResultToServer(searchValue);
      }

      if (checkSearchValue(searchValue) && hasWindow) {
        setLoading(true);

        // Добавляем юзеру куку, чтобы он перешел на новый поиск, а не старый
        setCookie("next_search", "1", {
          expires: 864000, // 10 дней
        });

        window.location.assign(`/search/?q=${encodeURIComponent(searchValue)}`);

        if (!isUserAnonymous) {
          saveSearchQuery(searchValue);
          setLastSearchResultToServer(searchValue);
        }

        return;
      }

      setError(true);
    },
    [searchValue, setError, isUserAnonymous, setLoading, setPopoverVisibility]
  );

  const handleOnFocus = (): void => {
    setPopoverVisibility(true);
  };

  const menuItems = useMemo(() => {
    if (resultsList) {
      return Array.from(resultsList.children);
    }
    return [];
  }, [resultsList]);

  const handleKeyPress = (event: Event): void => {
    const key = "key" in event ? event.key : "";

    if (menuItems.some(isFocusElement) && key === KeyCodes.Enter) {
      const el = document.activeElement;
      const hrefToItem =
        (el as HTMLAnchorElement).href ||
        `/search/?q=${encodeURIComponent(el?.textContent || "")}`;
      setPopoverVisibility(false);
      setLoading(true);
      window.location.assign(hrefToItem);
      return;
    }

    if (isPopoverVisibility && key) {
      if (
        key === KeyCodes.ArrowDown &&
        searchInputRef.current &&
        isFocusElement(searchInputRef.current)
      ) {
        focusFirstInElement(resultsList);
        return;
      }

      if (key === KeyCodes.ArrowDown) {
        const el = findFocussableNextSiblingElem(document.activeElement);
        if (!el || !menuItems.some(item => item === el)) {
          focusFirstInElement(resultsList);
        } else {
          focusFirstInElement(el);
          //@ts-ignore
          el.focus();
        }
      }

      if (key === KeyCodes.ArrowUp) {
        const el = findFocussablePrevSiblingElem(document.activeElement);
        if (!el || !menuItems.some(item => item === el)) {
          searchInputRef.current?.focus();
          searchInputRef.current?.setSelectionRange(
            searchValue.length,
            searchValue.length
          );
          return;
        } else {
          focusFirstInElement(el);
          //@ts-ignore
          el.focus();
        }
      }
    }
  };

  useEventListener("keydown", handleKeyPress);

  const hidePopover = (event: Event): void => {
    if (
      searchContainerRef.current &&
      (event.target === searchContainerRef.current ||
        searchContainerRef.current.contains(event.target as Node))
    ) {
      return;
    }
    setPopoverVisibility(false);
  };

  useEventListener("click", hidePopover);

  return (
    <div
      className={cn(styles.searchAB, { [styles.mobileSearch]: !isDesktop })}
      ref={searchContainerRef}
    >
      <Popover
        placement="bottom-start"
        content={
          checkSearchValue(searchValue) && searchResults?.length ? (
            <ResultsList
              searchResults={searchResults as SearchingResults["result"]}
              searchString={searchPhrase}
              resultsListRef={resultsListRefCallback}
              isUserAnonymous={isUserAnonymous}
              setLoading={setLoading}
              saveSearchQuery={saveSearchQuery}
              searchID={searchID}
              isDesktop={isDesktop}
              onClose={(): void => {
                setPopoverVisibility(false);
              }}
            />
          ) : (
            isUserHasHistory && (
              <SearchHistory
                history={lastSearch}
                setHistoryRefreshed={setHistoryRefreshed}
                searchHistoryListRef={resultsListRefCallback}
                deleteItemFromLastSearchStorage={
                  deleteItemFromLastSearchStorage
                }
                isUserAnonymous={isUserAnonymous}
                setLoading={setLoading}
                isDesktop={isDesktop}
                onClose={(): void => {
                  setPopoverVisibility(false);
                }}
              />
            )
          )
        }
        trigger={["none"]}
        contentClassName={styles.popoverContent}
        disableArrow
        offset={[0, 0]}
        hasMask
        visible={isPopoverVisibility}
        onVisibleChange={setPopoverVisibility}
        portal={false}
        visuallyHiddenDismiss
      >
        <>
          {isDesktop && (
            <SearchForm
              isLoading={isLoading}
              isError={isError}
              isSearchFocused={isPopoverVisibility}
              setPopoverVisibility={setPopoverVisibility}
              onFocus={handleOnFocus}
              onChange={handleOnChange}
              onClick={handleOnClick}
              onSubmit={handleSearchFormSubmit}
              inputRef={searchInputRef}
              value={searchValue}
              placeholder={getSearchPlaceholder(isSearchWidth, t)}
            />
          )}

          {!isDesktop && (
            <SimpleSearch
              onFocus={handleOnFocus}
              onChange={handleOnChange}
              onSubmit={handleSearchFormSubmit}
              handleClear={handleClear}
              inputRef={searchInputRef}
              searchValue={searchValue}
            />
          )}
        </>
      </Popover>

      <div className={styles.errorText}>
        {isError && t(SearchErrors.ShortValue)}
      </div>
    </div>
  );
};
